import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

const PRIVACY_DETAILS = [
  {
    title: "처리되는 정보",
    paragraphs: [
      "사용자가 입력한 질문과 최근 대화 맥락, 현재 메뉴·화면 정보, 검색조건과 선택값, 표시 컬럼 정보, 질문 처리에 필요한 집계·분석 결과가 처리될 수 있습니다.",
      "질문 시점의 검색조건이 대화이력에 포함되어 저장될 수 있습니다. 화면에 조회된 행 데이터는 대화이력에 저장하지 않습니다. 화면 그리드의 전체 행 원본은 외부 생성형 AI 서비스에 일괄 전송하지 않습니다.",
    ],
  },
  {
    title: "저장 및 이용 목적",
    paragraphs: [
      "대화 내용, 답변, 메뉴·화면 처리정보, 사용 모델 및 토큰 정보는 서비스 제공, 장애 대응, 보안 및 품질 개선을 위해 저장될 수 있습니다.",
      "피드백 여부와 관계없이 회사의 개발·운영 등 관련 업무 담당자가 업무상 필요한 범위에서 확인할 수 있습니다.",
    ],
  },
  {
    title: "외부 생성형 AI 서비스 이용",
    paragraphs: [
      "답변 생성에 필요한 정보는 OpenAI 등 외부 생성형 AI 서비스로 전송될 수 있습니다. 외부 제공자는 API로 전송된 데이터를 기본적으로 모델 학습에 사용하지 않지만, 보안·오남용 방지 등을 위해 제공자 정책에 따라 일정 기간 보관할 수 있습니다.",
      "국외에 위치한 외부 서비스가 이용되는 경우 개인정보보호법 제28조의8에 따라 정보가 국외로 이전될 수 있습니다. 이전 대상, 국가, 목적, 항목, 시점·방법, 보유기간 및 거부 방법은 회사 개인정보 처리방침에서 확인할 수 있습니다.",
    ],
  },
  {
    title: "Excel·CSV 첨부파일",
    paragraphs: [
      "현재 지원되는 Excel·CSV 표 파일은 서버에서 분석합니다. 질문 해석에 필요한 경우 컬럼명과 형식 정보만 외부 AI 서비스로 전송될 수 있으며, 데이터 값·파일명·시트명은 전송하지 않습니다.",
      "원본 업로드 데이터는 분석 후 보관하지 않으며, 분석용 데이터는 제한된 시간 동안 임시 보관됩니다.",
    ],
  },
  {
    title: "PDF·이미지 문서 첨부",
    paragraphs: [
      "PDF·PNG·JPEG 발주서를 첨부하면 문서 내용이 문서 분석을 위해 외부 AI 서비스로 전송됩니다. 전송된 내용은 모델 학습에 사용되지 않고 응답 후 저장되지 않는 설정으로 처리되며, 보안·오남용 방지 목적의 제한적 보관은 제공자 정책을 따릅니다.",
      "업로드한 원본 파일은 SNotes 서버에서 분석 완료 또는 실패 후 삭제되며, 추출된 항목은 제한된 시간 동안만 보관됩니다. 파일명은 서버로 전송하지 않습니다. 문서에 대한 질문과 답변은 다른 질문과 동일하게 대화이력에 저장됩니다.",
    ],
  },
  {
    title: "보유기간과 이용자 권리",
    paragraphs: [
      "정보의 보유기간과 파기 기준은 회사 개인정보 처리방침 및 관련 법령에 따르며, 보유기간이 지난 정보는 개인정보보호법 제21조에 따라 파기합니다.",
      "개인정보보호법 제35조부터 제37조까지에 따른 열람·정정·삭제·처리정지 요청은 회사 개인정보 처리방침에 기재된 담당 부서를 통해 할 수 있습니다.",
    ],
  },
] as const;

const ChatPrivacyNotice: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="chatbot-message chatbot-message-assistant chatbot-privacy-notice">
        <div className="chatbot-avatar" aria-hidden="true">
          <SmartToyIcon fontSize="small" />
        </div>
        <div className="chatbot-privacy-bubble">
          <div className="chatbot-privacy-heading">
            <InfoOutlinedIcon fontSize="inherit" />
            <strong>SNotes AI Bot 이용 안내</strong>
          </div>
          <div className="chatbot-privacy-summary">
            답변 생성을 위해 사용자의 질문과 질문 처리에 필요한 화면 정보 일부가
            외부 생성형 AI 서비스로 전송될 수 있습니다. 화면 그리드의 전체 행
            원본은 전송하지 않습니다. 대화와 관련 화면 처리정보는 서비스 운영 및
            품질 개선을 위해 저장될 수 있으며, 피드백 여부와 관계없이 회사의
            개발·운영 담당자가 업무상 필요한 범위에서 확인할 수 있습니다. 답변에
            불필요한 개인정보나 민감정보는 입력하지 마세요.
          </div>
          <button
            className="chatbot-privacy-detail-button"
            onClick={() => setOpen(true)}
            type="button"
          >
            자세히 보기
          </button>
        </div>
      </div>

      <Dialog
        aria-describedby="chatbot-privacy-description"
        aria-labelledby="chatbot-privacy-title"
        className="chatbot-privacy-dialog"
        fullWidth
        maxWidth="sm"
        onClose={() => setOpen(false)}
        open={open}
      >
        <DialogTitle
          className="chatbot-privacy-dialog-title"
          id="chatbot-privacy-title"
        >
          <span>생성형 AI 및 개인정보 처리 안내</span>
          <button
            aria-label="상세 안내 닫기"
            className="chatbot-privacy-dialog-close"
            onClick={() => setOpen(false)}
            title="닫기"
            type="button"
          >
            <CloseIcon fontSize="small" />
          </button>
        </DialogTitle>
        <DialogContent className="chatbot-privacy-dialog-content" dividers>
          <p className="chatbot-privacy-intro" id="chatbot-privacy-description">
            SNotes AI Bot은 사용자의 질문에 답하고 ERP 업무를 지원하기 위해
            개인정보보호법 제15조에 근거하여 다음 정보를 처리할 수 있습니다.
          </p>

          {PRIVACY_DETAILS.map((section, index) => (
            <section className="chatbot-privacy-section" key={section.title}>
              <h3>
                {index + 1}. {section.title}
              </h3>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <section className="chatbot-privacy-section chatbot-privacy-caution">
            <h3>6. 이용 시 주의사항</h3>
            <ul>
              <li>
                주민등록번호, 비밀번호, 인증정보, 계좌번호, 건강정보 등 답변에
                불필요한 개인정보나 민감정보를 입력하지 마세요.
              </li>
              <li>
                AI 답변은 참고용이며 오류가 포함될 수 있습니다. 업무 판단이나
                저장·승인 전에는 반드시 ERP 원본 데이터와 회사 절차를
                확인하세요.
              </li>
            </ul>
          </section>
          {/* 
          <p className="chatbot-privacy-footnote">
            이 안내는 핵심 내용을 요약한 것으로, 자세한 사항은 회사 개인정보
            처리방침을 따릅니다.
          </p>
          */}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatPrivacyNotice;
